import { SCHEMA_EDITOR_ID, type StageTabRole } from "./stagingModel";

// Which stage tabs hold work a draft save would leave behind (a stored
// draft carries only the pipeline code and the input document): unsaved
// script drafts, schema slot/instance drafts, and dirty tests. The
// Save-as-draft warning modal names these tabs.

export type UnsavedTab = "Source" | "Transform" | "Sink" | "Tests";

const TAB_ORDER: UnsavedTab[] = ["Source", "Transform", "Sink", "Tests"];

const TAB_OF_ROLE: Record<StageTabRole, UnsavedTab> = {
  source: "Source",
  transform: "Transform",
  sink: "Sink",
};

// A schema draft key names its block: an instance draft rides behind
// "schemaId|", the unattached slots encode their tab ("schema:editor"
// bare is the sink's legacy key, "@role" the source/transform ones), and
// a stage slot carries the stage id whose list resolves the tab.
export function schemaDraftTab(
  key: string,
  stageRole: (stageId: string) => StageTabRole | null,
): UnsavedTab | null {
  const blockKey = key.includes("|") ? key.slice(key.indexOf("|") + 1) : key;
  if (blockKey === SCHEMA_EDITOR_ID) return "Sink";
  const suffix = blockKey.slice(SCHEMA_EDITOR_ID.length + 1);
  if (suffix === "@source") return "Source";
  if (suffix === "@transform") return "Transform";
  if (suffix === "@sink") return "Sink";
  const roleOf = stageRole(suffix);
  return roleOf ? TAB_OF_ROLE[roleOf] : null;
}

export function unsavedWorkTabs(state: {
  scriptDrafts: Record<string, string>;
  schemaDrafts: Record<string, string>;
  dirtyTestCount: number;
  stageRole: (stageId: string) => StageTabRole | null;
}): UnsavedTab[] {
  const tabs = new Set<UnsavedTab>();
  if (Object.values(state.scriptDrafts).some((text) => text.trim() !== "")) {
    tabs.add("Transform");
  }
  for (const [key, text] of Object.entries(state.schemaDrafts)) {
    if (text.trim() === "") continue;
    const tab = schemaDraftTab(key, state.stageRole);
    if (tab) tabs.add(tab);
  }
  if (state.dirtyTestCount > 0) tabs.add("Tests");
  return TAB_ORDER.filter((tab) => tabs.has(tab));
}

/** "Source", "Source and Tests", "Source, Transform and Tests" — the
 *  warning modals' listing (tabs here, script names in unsavedScripts). */
export function listTabNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
